import { ChordQuality } from '../../../types';

export class VoiceLeading {
    private lastChordNotes: number[] = [];

    constructor() {
        this.reset();
    }

    public reset() {
        this.lastChordNotes = [];
    }

    /**
     * VOICING ENGINE (Harmonic Intelligence)
     * Finds the best inversion of the target chord to connect smoothly with the previous chord.
     */
    public getVoiceLedChord(targetNotes: number[], mood: 'Happy' | 'Sad' | 'Energetic' | 'Calm' | 'Mystery' = 'Calm', phraseFunction: 'Tension' | 'Resolution' | 'Neutral' = 'Neutral'): number[] {
        if (this.lastChordNotes.length === 0) {
            // Initial voicing based on Mood
            if (mood === 'Calm' || mood === 'Mystery') {
                // Open voicing: Spread the notes out (Root, 5th, 3rd + octave)
                const spread = [...targetNotes];
                if (spread.length >= 3) {
                    spread[1] += 12; // Move middle note up an octave
                    spread.sort((a, b) => a - b);
                }
                this.lastChordNotes = spread;
                return spread;
            }
            this.lastChordNotes = targetNotes;
            return targetNotes;
        }

        // Generate inversions
        const inversions: number[][] = [];
        const sortedTarget = [...targetNotes].sort((a, b) => a - b);

        // 1. Root Position
        inversions.push(sortedTarget);

        // 2. First Inversion
        const inv1 = [...sortedTarget];
        if (inv1.length > 0) {
            const first = inv1.shift()!;
            inv1.push(first + 12);
            inversions.push(inv1);
        }

        // 3. Second Inversion
        const inv2 = [...inv1];
        if (inv2.length > 0) {
            const second = inv2.shift()!;
            inv2.push(second + 12);
            inversions.push(inv2);
        }

        // Find the inversion with minimal total distance from lastChordNotes
        let bestInversion = inversions[0];
        let minDistance = Infinity;

        // PHRASE AWARENESS (Feature 8)
        // Resolution -> Strong bias towards Root Position (index 0)
        // Tension -> Bias towards Inversions (index 1, 2)
        const biasFactor = phraseFunction === 'Resolution' ? 20 : 0; // Penalty for non-root


        for (const inv of inversions) {
            let totalDist = 0;
            for (const nNew of inv) {
                let minDistForNote = Infinity;
                for (const nOld of this.lastChordNotes) {
                    const dist = Math.abs(nNew - nOld);
                    if (dist < minDistForNote) minDistForNote = dist;
                }
                totalDist += minDistForNote;
            }

            // Apply Bias
            if (phraseFunction === 'Resolution' && inv !== inversions[0]) {
                totalDist += biasFactor;
            }

            // AUDIT FIX 2.1: Parallel Fifths/Octaves Check
            totalDist += this.calculateParallelPenalty(this.lastChordNotes, inv);

            if (totalDist < minDistance) {
                minDistance = totalDist;
                bestInversion = inv;
            }
        }

        this.lastChordNotes = bestInversion;
        return bestInversion;
    }

    /**
     * SMART BASS (Harmonic Intelligence)
     * FEATURE 4: Living Bass (Linear Voice Leading)
     */
    public getSmartBassNote(
        scaleNotes: number[],
        rootNoteMidi: number,
        chordQuality: 'major' | 'minor' | 'diminished' | 'augmented' | 'sus2' | 'sus4' | 'dominant7' | 'minor7' = 'major',
        lastBassNote: number | null = null
    ): number {
        // Core Candidate: Root
        let candidates: number[] = [];

        // 1. Generate Octaves of Root (C2, C3)
        let base = rootNoteMidi;
        while (base < 36) base += 12; // C2 range
        candidates.push(base); // C2
        candidates.push(base + 12); // C3

        // 2. Generate 5th (Dominant)
        let interval5 = 7;
        if (chordQuality === 'diminished') interval5 = 6;
        if (chordQuality === 'augmented') interval5 = 8;
        candidates.push(base - 12 + interval5); // Low 5th
        candidates.push(base + interval5); // High 5th

        // 3. Generate 3rd (First Inversion)
        let interval3 = 4; // Major
        if (['minor', 'diminished', 'minor7'].includes(chordQuality)) interval3 = 3;
        if (chordQuality === 'sus2') interval3 = 2;
        if (chordQuality === 'sus4') interval3 = 5;

        candidates.push(base - 12 + interval3);
        candidates.push(base + interval3);

        // Filter: Keep within reasonable bass range (36 - 60 / C2 - C4)
        candidates = candidates.filter(n => n >= 36 && n <= 58);

        // DEFAULT (No History):
        if (lastBassNote === null) {
            candidates.sort((a, b) => Math.abs(a - 48) - Math.abs(b - 48));
            return candidates[0];
        }

        // LIVING BASS LOGIC:
        let bestNote = candidates[0];
        let bestScore = Infinity;

        for (const cand of candidates) {
            const dist = Math.abs(cand - lastBassNote);
            let score = dist;

            // Penalty for large leaps (> 5 semitones)
            if (dist > 5) score += 10;
            // Penalty for Tritone leap (6)
            if (dist === 6) score += 20;
            // Penalty for SAME note (mild)
            if (dist === 0) score += 2;

            if (score < bestScore) {
                bestScore = score;
                bestNote = cand;
            }
        }

        return bestNote;
    }

    /**
     * GLUE: Suspensions (Harmonic Intelligence)
     * Adds harmonic interest by delaying resolution.
     */
    public getSuspendedChord(
        chordNotes: number[],
        scaleNotes: number[],
        quality: ChordQuality,
        rootMidi: number,
        phraseFunction: 'Tension' | 'Resolution' | 'Neutral' = 'Neutral'
    ): { mainNotes: number[], resolutionNotes: { note: number, delayRatio: number }[] } {
        // Only suspend Major or Dominant7 chords
        if (quality !== 'major' && quality !== 'dominant7') {
            return { mainNotes: chordNotes, resolutionNotes: [] };
        }

        // Chance to suspend based on Phrase Function
        let probability = 0.25;
        if (phraseFunction === 'Tension') probability = 0.6; // High chance of suspension
        if (phraseFunction === 'Resolution') probability = 0.1; // Clean resolution preferred

        if (Math.random() > probability) {
            return { mainNotes: chordNotes, resolutionNotes: [] };
        }

        // Find the 3rd
        let thirdIndex = -1;
        let thirdNote = 0;

        for (let i = 0; i < chordNotes.length; i++) {
            let normInterval = (chordNotes[i] - rootMidi) % 12;
            if (normInterval < 0) normInterval += 12;

            if (normInterval === 4) {
                thirdIndex = i;
                thirdNote = chordNotes[i];
                break;
            }
        }

        if (thirdIndex === -1) {
            return { mainNotes: chordNotes, resolutionNotes: [] };
        }

        // AUDIT FIX 2.2: Only use Sus4, not Sus2
        // Sus4 resolves DOWN to the 3rd (theory-correct)
        // Sus2 would resolve UP which violates suspension resolution principles
        // 
        // Sus4 = 4th suspended, resolves down to 3rd
        // The 3rd is at +4 semitones from root, 4th is at +5 semitones
        // So we ADD 1 semitone to the 3rd to get the 4th
        const targetMidi = thirdNote + 1;
        const suspendedNote = this.findClosestInScale(scaleNotes, targetMidi);

        if (suspendedNote !== null) {
            const newChord = [...chordNotes];
            newChord[thirdIndex] = suspendedNote;

            // Resolution note is the original 3rd (resolves DOWN from 4th)
            return {
                mainNotes: newChord,
                resolutionNotes: [{ note: thirdNote, delayRatio: 0.5 }]
            };
        }

        return { mainNotes: chordNotes, resolutionNotes: [] };
    }

    private findClosestInScale(scaleNotes: number[], targetMidi: number): number | null {
        if (scaleNotes.length === 0) return null;
        let closest = scaleNotes[0];
        let minDiff = Math.abs(scaleNotes[0] - targetMidi);
        for (const note of scaleNotes) {
            const diff = Math.abs(note - targetMidi);
            if (diff < minDiff) {
                minDiff = diff;
                closest = note;
            }
        }
        return closest;
    }

    private calculateParallelPenalty(prevChord: number[], nextChord: number[]): number {
        const sortedPrev = [...prevChord].sort((a, b) => a - b);
        const sortedNext = [...nextChord].sort((a, b) => a - b);

        // Only check common voices (min length)
        const len = Math.min(sortedPrev.length, sortedNext.length);
        let penalty = 0;

        for (let i = 0; i < len; i++) {
            for (let j = i + 1; j < len; j++) {
                const intervalPrev = sortedPrev[j] - sortedPrev[i];
                const intervalNext = sortedNext[j] - sortedNext[i];

                // Perfect Fifth (7) or Octave (12, 0=Unison)
                // Note: We check modulo 12 to catch compound intervals (19th = 5th)
                const absInt = Math.abs(intervalNext);
                const isPerfectInterval = (absInt % 12 === 7) || (absInt % 12 === 0);

                if (isPerfectInterval && intervalPrev === intervalNext) {
                    // Check if voices actually moved (not static)
                    if (sortedNext[i] !== sortedPrev[i]) {
                        penalty += 100; // Heavy penalty for parallel perfect intervals
                    }
                }
            }
        }
        return penalty;
    }
}
